import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import AudioRecorder from "../AudioRecorder";
import Icon from "../../../components/common/Icon";
import styles from "./CloneVoice.module.css";

interface CloneVoiceUploadProps {
    onUploadComplete: (files: File[]) => void;
    disabled?: boolean;
}

const CloneVoiceUpload: React.FC<CloneVoiceUploadProps> = ({ onUploadComplete, disabled = false }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [dragActive, setDragActive] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [showRecorder, setShowRecorder] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(Array.from(e.dataTransfer.files));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(Array.from(e.target.files));
        }
    };

    const handleFiles = (files: File[]) => {
        const validTypes = ["audio/", "video/"];
        const maxSize = 10 * 1024 * 1024; // 10MB per file

        for (const file of files) {
            const isValid = validTypes.some((type) => file.type.startsWith(type));
            if (!isValid) {
                alert(t("pleaseUploadAudioOrVideoFile"));
                return;
            }
            if (file.size > maxSize) {
                alert(t("fileTooLarge"));
                return;
            }
        }

        setUploadedFiles((prev) => [...prev, ...files]);
    };

    const removeFile = (index: number) => {
        setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleClick = () => {
        if (disabled) return;
        fileInputRef.current?.click();
    };

    const handleRecordingComplete = (audioFile: File) => {
        setUploadedFiles((prev) => [...prev, audioFile]);
        setShowRecorder(false);
    };

    const handleNext = () => {
        if (uploadedFiles.length === 0) return;
        // Just pass the files, upload will happen later when saving
        onUploadComplete(uploadedFiles);
    };

    return (
        <>
            {showRecorder && (
                <AudioRecorder onRecordingComplete={handleRecordingComplete} onClose={() => setShowRecorder(false)} />
            )}

            <div className={styles.cloneVoiceTips}>
                <div className={styles.tipCard}>
                    <Icon name="ic-ban" className={styles.tipIcon} />
                    <h4>{t("avoidNoisyEnvironments")}</h4>
                    <p>{t("backgroundSoundsInterfere")}</p>
                </div>
                <div className={styles.tipCard}>
                    <Icon name="ic-thumbs-up" className={styles.tipIcon} />
                    <h4>{t("checkMicrophoneQuality")}</h4>
                    <p>{t("tryExternalUnits")}</p>
                </div>
                <div className={styles.tipCard}>
                    <Icon name="ic-mic" className={styles.tipIcon} />
                    <h4>{t("useConsistentEquipment")}</h4>
                    <p>{t("dontChangeRecordingEquipment")}</p>
                </div>
            </div>

            <div
                className={`${styles.dropzone} ${dragActive ? styles.dragActive : ""} ${disabled ? styles.disabled : ""}`}
                onDragEnter={disabled ? undefined : handleDrag}
                onDragLeave={disabled ? undefined : handleDrag}
                onDragOver={disabled ? undefined : handleDrag}
                onDrop={disabled ? undefined : handleDrop}
                onClick={disabled ? undefined : handleClick}
                style={{ cursor: disabled ? "not-allowed" : "pointer" }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*,video/*"
                    multiple
                    onChange={handleChange}
                    style={{ display: "none" }}
                />

                {uploadedFiles.length === 0 ? (
                    <>
                        <Icon name="ic-upload-cloud" className={styles.uploadIcon} />
                        <p className={styles.uploadText}>{t("clickToUploadOrDragAndDrop")}</p>
                        <p className={styles.uploadSubtext}>{t("multipleAudioFilesSupported")}</p>
                        <p className={styles.uploadOr}>{t("or")}</p>
                        <button
                            className={styles.recordButton}
                            disabled={disabled}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!disabled) {
                                    setShowRecorder(true);
                                }
                            }}
                        >
                            <Icon name="ic-mic" />
                            {t("recordAudio")}
                        </button>
                    </>
                ) : (
                    <div className={styles.filesContainer}>
                        {uploadedFiles.map((file, index) => (
                            <div key={index} className={styles.fileUploaded}>
                                <div className={styles.fileIcon}>✅</div>
                                <p className={styles.fileName}>{file.name}</p>
                                <p className={styles.fileSize}>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                <button
                                    className={styles.removeFileButton}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(index);
                                    }}
                                >
                                    {t("remove")}
                                </button>
                            </div>
                        ))}
                        <div className={styles.addMoreButtonsContainer}>
                            <button
                                className={styles.addMoreButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    fileInputRef.current?.click();
                                }}
                            >
                                📁 {t("addMoreFiles")}
                            </button>
                            <button
                                className={styles.addMoreButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowRecorder(true);
                                }}
                            >
                                🎤 {t("recordMore")}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.cloneVoiceFooter}>
                <div className={styles.audioRequirement}>
                    <input type="checkbox" disabled checked={uploadedFiles.length > 0} />
                    <span>{t("tenSecondsOfAudioRequired")}</span>
                </div>
                <button className={styles.nextButton} disabled={uploadedFiles.length === 0} onClick={handleNext}>
                    {t("next")}
                    {uploadedFiles.length > 0 && ` (${uploadedFiles.length})`}
                </button>
            </div>
        </>
    );
};

export default CloneVoiceUpload;
